import { queryOptions, useQuery } from '@tanstack/react-query'

import { tradingProductFetcher } from '@/libs/fetcher/presets'

/**
 * Public equity-baskets offers API for the Stocks > Baskets sub-tab, verified
 * against
 * GET https://ansta-trading-product-api.3ona.co/api/public/v1/equity-baskets/offers
 * on 2026-09-07:
 * - No auth. `sort_by=1y_performance` is honored (percentage desc) — used to
 *   match cdc-web's WhaleBasketList ordering (2026-09-08).
 * - Cursor pagination: `?page_size=N&page_token=T`, top-level
 *   `pagination: { has_more, next_page_token }`. The list is loaded in full
 *   (no UI paging), following the has_more/next_page_token chain.
 * - `performance.percentage` is a percentage number as a string
 *   (e.g. "10.47" means +10.47%) and is always paired with period
 *   "1y_performance" in practice; `performance` itself can be null.
 * - Only non-deleted, active baskets (status 1, is_deleted false) are shown.
 */
export interface EquityBasketAsset {
  asset_id: string // e.g. 'S_AAPL.O'
  display_symbol: string // e.g. 'AAPL'
  icon_url?: string | null
  name?: string | null
  weight?: string | null
}

export interface EquityBasketOffer {
  id: string
  title: string
  banner_url?: string | null
  assets?: EquityBasketAsset[] | null
  performance?: {
    percentage?: string | null
    period?: string | null
  } | null
  status?: number | null
  is_deleted?: boolean | null
}

const BASKETS_PAGE_SIZE = 100

interface EquityBasketsOffersResponse {
  equity_baskets?: EquityBasketOffer[] | null
  pagination?: {
    has_more?: boolean
    next_page_token?: string | null
  } | null
}

async function fetchEquityBasketOffersPage(
  pageToken?: string,
): Promise<EquityBasketsOffersResponse> {
  const response = await tradingProductFetcher<EquityBasketsOffersResponse>(
    '/equity-baskets/offers',
    {
      method: 'GET',
      // Freshness is handled by React Query (staleTime below); no polling and
      // no Next.js server fetch cache.
      cache: 'no-store',
      queries: {
        page_size: BASKETS_PAGE_SIZE,
        // Server-side sort by 1-year performance desc — same as cdc-web's
        // WhaleBasketList (its default is 3m, but this tab fixes the period to
        // 1y per WWW-5085 Q8). The public API honors sort_by, so a local
        // re-sort would needlessly diverge from upstream ordering of ties.
        sort_by: '1y_performance',
        ...(pageToken ? { page_token: pageToken } : {}),
      },
    },
  )
  return response ?? {}
}

/**
 * Fetches every basket page by following the cursor chain (no UI paging per
 * WWW-5085).
 */
export async function fetchAllEquityBasketOffers(): Promise<EquityBasketOffer[]> {
  const offers: EquityBasketOffer[] = []
  let pageToken: string | undefined
  for (;;) {
    const page = await fetchEquityBasketOffersPage(pageToken)
    offers.push(...(page.equity_baskets ?? []))
    const nextToken = page.pagination?.next_page_token ?? undefined
    if (!page.pagination?.has_more || !nextToken) break
    pageToken = nextToken
  }
  return offers.filter(b => !b.is_deleted && b.status === 1)
}

/** Baskets list is fetched once and not polled (decision 2026-09-07): basket
 *  contents change rarely, and freshness on tab revisit is bounded by
 *  staleTime + refetchOnMount. */
export const equityBasketsQueryOptions = queryOptions({
  queryKey: ['equityBaskets'],
  queryFn: () => fetchAllEquityBasketOffers(),
  staleTime: 60_000,
})

export const useEquityBaskets = () => useQuery(equityBasketsQueryOptions)
